import React, { useEffect, useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import {
  Group,
  Insights,
  TrendingUp,
  ConfirmationNumber,
  MonetizationOn,
  Sync,
  Payments,
  Storefront,
  TrendingDown,
  AccountBalance,
  Undo,
  Receipt,
  CalendarToday,
  Assessment,
  Person2,
} from "@mui/icons-material";
import ReportTable from "./ReportTable";
import "../../styles/report.css";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllCustomersRequest } from "../../redux/Customers/actions/customerActions";
import { fetchAllVendorsRequest } from "../../redux/Vendors/actions/vendorActions";
import {
  fetchAllInvoicesRequest,
  fetchInvoiceViewsRequest,
} from "../../redux/Invoice/InvoiceAction";
import { fetchAllBookingRequest } from "../../redux/Bookings/BookingAction";
import {
  clearAnnualReports,
  fetchAnnualReportsRequest,
} from "../../redux/annualReports/annualReportsActions";
import DynamicYearSelector from "./DynamicYearSelector";

const AdminReports = () => {
  const dispatch = useDispatch();

  // Existing selectors
  const { customers } = useSelector((state) => state.customers);
  const { vendors } = useSelector((state) => state.vendors);
  const { invoices } = useSelector((state) => state.Invoice);
  const bookings = useSelector((state) => state.booking.bookings || []);
  const { user } = useSelector((state) => state.auth);
  const annualReports = useSelector((state) => state.annualReports);

  // State
  const [activeTab, setActiveTab] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [apiCallTrigger, setApiCallTrigger] = useState(0); // Add trigger for API calls

  // Annual Reports state
  const [fromYear, setFromYear] = useState(new Date().getFullYear() - 1);
  const [toYear, setToYear] = useState(new Date().getFullYear());
  const [tempFromYear, setTempFromYear] = useState(
    new Date().getFullYear() - 1,
  );
  const [tempToYear, setTempToYear] = useState(new Date().getFullYear());
  const [annualFromDate, setAnnualFromDate] = useState(null);
  const [annualToDate, setAnnualToDate] = useState(null);
  const [tempAnnualFromDate, setTempAnnualFromDate] = useState(null);
  const [tempAnnualToDate, setTempAnnualToDate] = useState(null);

  // Helper functions
  const getTodayDate = () => new Date().toISOString().split("T")[0];
  const getCurrentYear = () => new Date().getFullYear();

  // Event handlers
  const handleTabChange = (event, newTab) => {
    setActiveTab(newTab);
    setApiCallTrigger((prev) => prev + 1); // Trigger API call for new tab
  };

  // Centralized API call function - Updated to match new tab indices
  const triggerApiCalls = (tabIndex = activeTab) => {
    switch (tabIndex) {
      case 0: // Payments
        // dispatch(
        //   fetchAllInvoicesRequest({
        //     pageNo: 1,
        //     pagesize: 10,
        //     pagination_required: true,
        //   })
        // );
        break;
      case 1: // Sales
        // dispatch(
        //   fetchAllInvoicesRequest({
        //     pageNo: 1,
        //     pagesize: 10,
        //     pagination_required: true,
        //     type: "yearly",
        //     date_from: `${selectedYear}-01-01`,
        //   })
        // );
        break;
      case 2: // Finance
        // Prepare the request payload
        const requestPayload = {};

        // If dates are selected, use dates only (no year filters)
        if (annualFromDate && annualToDate) {
          requestPayload.from_date = annualFromDate.toISOString().split("T")[0];
          requestPayload.to_date = annualToDate.toISOString().split("T")[0];
        }
        // If dates are not selected, use year filters
        else {
          requestPayload.from_year = fromYear;
          requestPayload.to_year = toYear;
        }

        dispatch(fetchAnnualReportsRequest(requestPayload));
        break;
      default:
        break;
    }
  };

  // Initial load - only call APIs for the current active tab
  useEffect(() => {
    triggerApiCalls(activeTab);
  }, [
    dispatch,
    activeTab,
    selectedMonth,
    selectedYear,
    fromYear,
    toYear,
    annualFromDate,
    annualToDate,
  ]);

  // Trigger API calls when tab changes
  useEffect(() => {
    if (apiCallTrigger > 0) {
      triggerApiCalls(activeTab);
    }
  }, [apiCallTrigger]);

  // Calculations
  const activeCustomersCount = customers?.activeCustomerCount || 0;
  const totalRecord = useSelector((state) => state.booking?.totalRecords || []);
  const activeVendorsCount = vendors?.activeVendorCount || 0;
  const newCustomersThisMonth = customers?.thisMonthCustomerCount || 0;
  const newVendorsThisMonth = vendors?.thisMonthVendorCount || 0;

  const calculatePaymentStats = () => {
    if (!invoices?.data || !Array.isArray(invoices.data))
      return { total: 0, pending: 0, completed: 0 };

    const total = invoices.data.reduce(
      (sum, booking) => sum + (booking.Order?.total || 0),
      0,
    );
    const pending = invoices.data
      .filter((booking) => booking.payment_status === 0)
      .reduce((sum, booking) => sum + (booking.Order?.total || 0), 0);
    const completed = invoices.data
      .filter((booking) => booking.payment_status === 1)
      .reduce((sum, booking) => sum + (booking.Order?.total || 0), 0);

    return { total, pending, completed };
  };

  const calculateSalesStats = () => {
    if (!invoices?.data || !Array.isArray(invoices.data))
      return { total: 0, domestic: 0, international: 0, transit: 0 };

    const total = invoices.totalServicesSold || 0;
    const domestic = invoices.domesticServicesCount || 0;
    const international = invoices.internationalServicesCount || 0;
    const transit = invoices.transitServicesCount || 0;

    return { total, domestic, international, transit };
  };

  const paymentStats = calculatePaymentStats();
  const salesStats = calculateSalesStats();

  // Validation and Annual Reports handlers (keeping existing logic)
  const validateDateRange = () => {
    const currentYear = getCurrentYear();
    const today = new Date();

    if (tempFromYear > tempToYear) {
      alert("From Year cannot be greater than To Year");
      return false;
    }

    const maxAllowedToYear = currentYear + 1;

    if (tempFromYear > currentYear) {
      alert("From Year cannot be in the future");
      return false;
    }

    if (tempToYear > maxAllowedToYear) {
      alert("To Year cannot be more than one year in the future");
      return false;
    }

    if (tempAnnualFromDate && tempAnnualToDate) {
      if (tempAnnualFromDate > tempAnnualToDate) {
        alert("From Date cannot be greater than To Date");
        return false;
      }

      if (tempAnnualFromDate > today || tempAnnualToDate > today) {
        alert("Dates cannot be in the future");
        return false;
      }
    }

    if (tempAnnualFromDate && tempAnnualFromDate > today) {
      alert("From Date cannot be in the future");
      return false;
    }

    if (tempAnnualToDate && tempAnnualToDate > today) {
      alert("To Date cannot be in the future");
      return false;
    }

    return true;
  };

  const handleAnnualReportsSubmit = () => {
    if (tempFromYear && tempToYear) {
      if (!validateDateRange()) {
        return;
      }

      setFromYear(tempFromYear);
      setToYear(tempToYear);
      setAnnualFromDate(tempAnnualFromDate);
      setAnnualToDate(tempAnnualToDate);
    } else {
      alert("Please select both From Year and To Year");
    }
  };

  const handleClearAnnualReports = () => {
    setFromYear(new Date().getFullYear() - 1);
    setToYear(new Date().getFullYear());
    setTempFromYear(new Date().getFullYear() - 1);
    setTempToYear(new Date().getFullYear());
    setAnnualFromDate(null);
    setAnnualToDate(null);
    setTempAnnualFromDate(null);
    setTempAnnualToDate(null);
    dispatch(clearAnnualReports());
  };

  const handleFromYearChange = (year) => {
    const currentYear = getCurrentYear();
    if (year <= currentYear) {
      setTempFromYear(year);
      if (year > tempToYear) {
        setTempToYear(year);
      }
    }
  };

  const handleToYearChange = (year) => {
    const currentYear = getCurrentYear();
    const maxAllowedYear = currentYear + 1;

    if (year <= maxAllowedYear && year >= tempFromYear) {
      setTempToYear(year);
    }
  };

  const handleFromDateChange = (e) => {
    const selectedDate = e.target.value ? new Date(e.target.value) : null;
    const today = new Date();

    if (selectedDate && selectedDate <= today) {
      setTempAnnualFromDate(selectedDate);
      if (tempAnnualToDate && selectedDate > tempAnnualToDate) {
        setTempAnnualToDate(null);
      }
    } else if (!selectedDate) {
      setTempAnnualFromDate(null);
    }
  };

  const handleToDateChange = (e) => {
    const selectedDate = e.target.value ? new Date(e.target.value) : null;
    const today = new Date();

    if (selectedDate && selectedDate <= today) {
      if (!tempAnnualFromDate || selectedDate >= tempAnnualFromDate) {
        setTempAnnualToDate(selectedDate);
      }
    } else if (!selectedDate) {
      setTempAnnualToDate(null);
    }
  };

  const getSummaryData = () => {
    switch (activeTab) {
      case 0: // Payments (was previously case 3)
        return [
          {
            label: "Total Revenue",
            value: `₹${invoices?.totalPaidAmount?.toFixed(2) || "0.00"}`,
            icon: <MonetizationOn sx={{ color: "#4361ee" }} />,
          },
          {
            label: "Pending Payments",
            value: `₹${paymentStats.pending.toFixed(2)}`,
            icon: <Sync sx={{ color: "#f72585" }} />,
          },
          {
            label: "Completed Payments",
            value: `₹${invoices?.totalPaidAmount?.toFixed(2) || "0.00"}`,
            icon: <Payments sx={{ color: "#4cc9f0" }} />,
          },
        ];

      case 1: // Sales (was previously case 4)
        return [
          {
            label: "Total Services Sold",
            value: salesStats.total,
            icon: <Storefront sx={{ color: "#4361ee" }} />,
          },
          {
            label: "Domestic Services",
            value: salesStats.domestic,
            icon: <Insights sx={{ color: "#f72585" }} />,
          },
          {
            label: "International Services",
            value: salesStats.international,
            icon: <TrendingUp sx={{ color: "#4cc9f0" }} />,
          },
          {
            label: "Transit Services",
            value: salesStats.transit,
            icon: <TrendingUp sx={{ color: "#f0740eff" }} />,
          },
        ];

      case 2: // Finance (was previously case 5)
        return [
          {
            label: "Total Reports",
            value: annualReports.count || "0",
            icon: <Assessment sx={{ color: "#4361ee" }} />,
          },
          {
            label: "Total Bookings",
            value: annualReports.totalBooking || "0",
            icon: <ConfirmationNumber sx={{ color: "#f72585" }} />,
          },
          {
            label: "Total Services",
            value: annualReports?.data?.full_total_service_count || "0",
            icon: <Person2 sx={{ color: "#0ebcf1ff" }} />,
          },
          {
            label: "Total Revenue",
            value: `₹${(annualReports.totalRevenue || 0).toFixed(2)}`,
            icon: <MonetizationOn sx={{ color: "#ec5c09ff" }} />,
          },
          {
            label: "Period",
            value: `${fromYear} - ${toYear}`,
            icon: <CalendarToday sx={{ color: "#1a0569ff" }} />,
          },
        ];

      default:
        return [];
    }
  };

  return (
    <Box className="report-dashboard">
      {/* Tab Navigation - Only 3 tabs now */}
      <Box className="tabs">
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="Report Tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Payments" />
          <Tab label="Sales" />
          <Tab label="Finance" />
        </Tabs>
      </Box>

      {/* Annual Reports Year and Date Range Selector - Only show for Finance tab (index 2) */}
      {activeTab === 2 && (
        <Box
          className="annual-reports-selector"
          sx={{ mb: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexWrap: "wrap",
              justifyContent: "space-between",
            }}
          >
            {/* Year Range Selection */}
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}
              >
                Year:
              </Typography>
              <DynamicYearSelector
                selectedYear={tempFromYear}
                setSelectedYear={handleFromYearChange}
                maxYear={getCurrentYear()}
                isToYear={false}
                isFinanceReport={true}
              />
              <Typography variant="body2" sx={{ mx: 0.5 }}>
                to
              </Typography>
              <DynamicYearSelector
                selectedYear={tempToYear}
                setSelectedYear={handleToYearChange}
                minYear={tempFromYear}
                maxYear={getCurrentYear()}
                isToYear={true}
                isFinanceReport={true}
              />
            </Box>
            {/* Date Range Selection */}
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}
              >
                Date:
              </Typography>
              <TextField
                label="From Date"
                type="date"
                value={
                  tempAnnualFromDate
                    ? tempAnnualFromDate.toISOString().split("T")[0]
                    : ""
                }
                onChange={handleFromDateChange}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: getTodayDate(),
                }}
                size="small"
                sx={{ minWidth: 140 }}
              />
              <TextField
                label="To Date"
                type="date"
                value={
                  tempAnnualToDate
                    ? tempAnnualToDate.toISOString().split("T")[0]
                    : ""
                }
                onChange={handleToDateChange}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: tempAnnualFromDate
                    ? tempAnnualFromDate.toISOString().split("T")[0]
                    : undefined,
                  max: getTodayDate(),
                }}
                size="small"
                sx={{ minWidth: 140 }}
                disabled={!tempAnnualFromDate}
              />
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAnnualReportsSubmit}
                size="small"
                disabled={!tempFromYear || !tempToYear || annualReports.loading}
              >
                {annualReports.loading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  "Apply"
                )}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleClearAnnualReports}
                size="small"
              >
                Clear
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Vertical Summary Section */}
      <Box className="summary-section">
        {getSummaryData().map((item, index) => (
          <Box key={index} className="metric">
            <Box className="icon">{item.icon}</Box>
            <Typography variant="h6">{item.label}</Typography>
            <Typography variant="body1">{item.value}</Typography>
          </Box>
        ))}
      </Box>

      {/* Annual Reports Table - Only show for Finance tab (index 2) */}
      {activeTab === 2 &&
        annualReports.rows &&
        annualReports.rows.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Annual Reports Details
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Date</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Total Bookings</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Total Revenue</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Created At</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {annualReports.rows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>
                        {new Date(row.date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>{row.total_booking}</TableCell>
                      <TableCell>₹{row.total_revenue.toFixed(2)}</TableCell>
                      <TableCell>
                        {new Date(row.created_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

      {/* Empty State for Annual Reports - Only for Finance tab (index 2) */}
      {activeTab === 2 &&
        !annualReports.loading &&
        annualReports.rows.length === 0 && (
          <Box sx={{ textAlign: "center", mt: 4, py: 4 }}>
            <Assessment sx={{ fontSize: 64, color: "#ccc", mb: 2 }} />
            <Typography variant="h6" color="textSecondary">
              No annual reports found
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Try adjusting the year range or date filters to see more data
            </Typography>
          </Box>
        )}

      {/* Report Content - Hide table for Finance tab (index 2) */}
      {activeTab !== 2 && (
        <Box className="report-content">
          <ReportTable
            type={
              activeTab === 0
                ? "payments"
                : activeTab === 1
                  ? "sales"
                  : "default"
            }
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
            setSelectedMonth={setSelectedMonth}
            setSelectedYear={setSelectedYear}
            skipInitialApiCall={true} // Add this prop to prevent duplicate API calls
          />
        </Box>
      )}
    </Box>
  );
};

export default AdminReports;
